import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  fetchConfigList,
  createConfigListItem,
  updateConfigListItem,
  deleteConfigListItem,
} from "../api/apiClient";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useTheme } from "@mui/material/styles";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";

const LIST_KEYS = [
  { key: "COURSE_CATEGORY", label: "Course Categories" },
  { key: "SKILL_CATEGORY", label: "Skill / Knowledge Categories" },
  { key: "TEACHING_ROLE", label: "Teaching Roles" },
];

const DS = {
  surfaceContainerLowest: "#ffffff",
  surfaceContainerLow: "var(--color-surface-press-light)",
  surfaceContainerHigh: "var(--color-hairline-cloud)",
  onSurface: "#191c1e",
  onSurfaceVariant: "var(--color-ink-2)",
};

export default function ConfigListManagementView() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const { tenantId } = useActiveTenant();

  const [activeKey, setActiveKey] = useState(LIST_KEYS[0].key);
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");

  const load = useCallback(() => {
    if (!tenantId) return;
    fetchConfigList(tenantId, activeKey).then(setItems);
  }, [tenantId, activeKey]);

  useEffect(() => {
    load();
  }, [load]);

  function openAdd() {
    setEditing(null);
    setValue("");
    setDialogOpen(true);
  }

  function openEdit(item) {
    setEditing(item);
    setValue(item.value);
    setDialogOpen(true);
  }

  function handleSave() {
    if (!tenantId || !value.trim()) return;
    const promise = editing
      ? updateConfigListItem(tenantId, activeKey, editing.id, { value: value.trim() })
      : createConfigListItem(tenantId, activeKey, { value: value.trim() });
    promise.then(() => {
      load();
      setDialogOpen(false);
      setEditing(null);
      setValue("");
    });
  }

  function handleDelete(item) {
    if (!tenantId) return;
    if (window.confirm(`Remove "${item.value}"?`)) {
      deleteConfigListItem(tenantId, activeKey, item.id).then(load);
    }
  }

  if (!tenantId) {
    return (
      <Box>
        <NoTenantSelected message="Select a training center from the top bar before managing dropdown lists." />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color={tPrimary} sx={{ mb: 1, fontFamily: "var(--font-display)" }}>
          Manage Dropdown Lists
        </Typography>
        <Typography variant="body1" color={DS.onSurfaceVariant}>
          Configure the category, role, and type options offered throughout this training center.
        </Typography>
      </Box>

      <Tabs
        value={activeKey}
        onChange={(e, v) => setActiveKey(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: `1px solid ${DS.surfaceContainerHigh}` }}
      >
        {LIST_KEYS.map((lk) => (
          <Tab key={lk.key} value={lk.key} label={lk.label} sx={{ fontWeight: 600, textTransform: "none" }} />
        ))}
      </Tabs>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: "12px",
          bgcolor: DS.surfaceContainerLowest,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color={DS.onSurface} sx={{ fontFamily: "var(--font-display)" }}>
          {items.length} {LIST_KEYS.find((lk) => lk.key === activeKey)?.label}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ borderRadius: "8px", bgcolor: tPrimary, fontWeight: 600, "&:hover": { bgcolor: tPrimaryDark } }}
        >
          Add Value
        </Button>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "12px",
          bgcolor: DS.surfaceContainerLowest,
          boxShadow: "none",
          border: `1px solid ${DS.surfaceContainerHigh}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: DS.surfaceContainerLow }}>
              <TableCell sx={{ fontWeight: 700, py: 2.5, color: DS.onSurface }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2.5, textAlign: "right", color: DS.onSurface }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover sx={{ "&:hover": { bgcolor: DS.surfaceContainerLow } }}>
                <TableCell>
                  <Typography fontWeight={600} color={DS.onSurface}>
                    {item.value}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: tPrimary }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(item)} sx={{ color: "var(--color-danger)" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                  <ListAltIcon sx={{ fontSize: 48, color: DS.onSurfaceVariant, mb: 2 }} />
                  <Typography color={DS.onSurfaceVariant}>No values defined yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: tPrimary }}>
          {editing ? "Edit Value" : "Add Value"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!value.trim()} sx={{ bgcolor: tPrimary, "&:hover": { bgcolor: tPrimaryDark } }}>
            {editing ? "Save Changes" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
